import React, { useEffect, useState, useContext } from 'react';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Linkedin } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { motion } from 'framer-motion';
import { useColleagues } from '@/context/ColleagueContext';
import { useTestimonials } from '@/context/TestimonialContext';
import { Helmet } from 'react-helmet';

const Testimonials = () => {
  const { testimonials, loading, error, refetchTestimonials } = useTestimonials();
  const { colleagues, refetchColleagues } = useColleagues();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    refetchTestimonials();
    refetchColleagues();
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };



  const topRowColleagues = colleagues.slice(0, 6);
  const bottomRowColleagues = colleagues.slice(6, 10);



  return (
    <>
    <Helmet>
    <title>Mopaossi | Services</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
    <PageLayout
      title="Mopaossi | Collègues"
      description="Ce que j'ai pu obtenir de mon réseau..."
    >
      <div className="space-y-16">
        {/* Colleagues section */}
        <section>
          <h2 className="text-3xl font-bold mb-8 text-center">Mon Réseau Linkedin à son mot à dire...</h2>
          
          <div className="flex flex-col items-center space-y-6">
            {/* Top row - 6 colleagues */}
            <div className="flex justify-center gap-6 flex-wrap">
              {topRowColleagues.map((colleague, index) => (
                <div 
                  key={index} 
                  className="relative group"
                  onClick={() => window.open(colleague.linkedinUrl, '_blank')}
                >
                  <div className="w-16 h-16 rounded-full overflow-hidden cursor-pointer transition-all duration-300">
                    <img 
                      src={colleague.image} 
                      alt={colleague.name} 
                      className="w-full h-full object-cover group-hover:opacity-30 transition-opacity duration-300"
                    />
                    <div className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-leo-blue transition-opacity duration-300">
                    <Linkedin fill="white" strokeWidth={0} className="text-white" size={24} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Bottom row - 4 colleagues */}
            <div className="flex justify-center gap-6 flex-wrap">
              {bottomRowColleagues.map((colleague, index) => (
                <div 
                  key={index} 
                  className="relative group"
                  onClick={() => window.open(colleague.linkedinUrl, '_blank')}
                >
                  <div className="w-16 h-16 rounded-full overflow-hidden cursor-pointer transition-all duration-300">
                    <img 
                      src={colleague.image} 
                      alt={colleague.name} 
                      className="w-full h-full object-cover group-hover:opacity-30 transition-opacity duration-300"
                    />
                    <div className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-leo-blue transition-opacity duration-300">
                      <Linkedin fill="white" strokeWidth={0} className="text-white" size={24} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials carousel */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Témoignages</h2>
          
          {loading ? (
            <div className="text-center py-8">Chargement des témoignages...</div>
          ) : testimonials.length > 0 ? (
            <div className="relative max-w-3xl mx-auto">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-lg shadow-lg p-8"
              >
                <div className="flex flex-col items-center text-center">
                  <Avatar className="w-20 h-20 mb-4">
                    <AvatarImage src={testimonials[currentTestimonial]?.image} alt={testimonials[currentTestimonial]?.name} />
                    <AvatarFallback>{testimonials[currentTestimonial]?.name?.charAt(0) || "U"}</AvatarFallback>
                  </Avatar>
                  
                  <h3 className="text-2xl font-bold mb-1">{testimonials[currentTestimonial]?.name}</h3>
                  <p className="text-gray-600 mb-6">{testimonials[currentTestimonial]?.role} at {testimonials[currentTestimonial]?.company}</p>
                  
                  <p className="text-xl italic">{testimonials[currentTestimonial]?.content}</p>
                </div>
              </motion.div>

              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={prevTestimonial}
                  className="rounded-full"
                >
                  <ChevronLeft />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={nextTestimonial}
                  className="rounded-full"
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">Aucun témoignage disponible pour le moment.</div>
          )}
        </section>
      </div>
    </PageLayout>
    </>
  );
};

export default Testimonials;

